import logo from 'assets/images/logotype/Logo_Stand_Red.svg';
import { marketingHref, marketingLinks } from 'utils/siteLinks';
import { openCookieSettings } from 'utils/cookieConsent';

import styles from './Footer.module.scss';

/**
 * Логотипы соцсетей: Font Awesome Free 7 (brands: vk, telegram), лицензия CC BY 4.0
 * https://fontawesome.com/license/free. Путь вставлен как есть, чтобы не тянуть пакет brands целиком.
 */
const SOCIAL_ICONS = {
  vk: { viewBox: '0 0 448 512', d: 'M31.5 63.5C0 95 0 145.7 0 247L0 265C0 366.3 0 417 31.5 448.5S113.7 480 215 480l17.9 0c101.4 0 152.1 0 183.5-31.5S448 366.3 448 265l0-17.9c0-101.4 0-152.1-31.5-183.5S334.3 32 233 32L215 32C113.7 32 63 32 31.5 63.5zM75.6 168.3l51.1 0c1.7 85.5 39.4 121.7 69.3 129.2l0-129.2 48.2 0 0 73.7c29.5-3.2 60.5-36.8 70.9-73.7l48.2 0c-3.9 19.2-11.8 37.3-23.1 53.3s-25.7 29.5-42.5 39.6c18.7 9.3 35.2 22.4 48.4 38.5s22.9 34.9 28.3 55l-53 0c-4.9-17.5-14.8-33.1-28.6-45s-30.7-19.4-48.7-21.6l0 66.6-5.8 0c-102.1 0-160.3-70-162.8-186.5z' },
  tg: { viewBox: '0 0 512 512', d: 'M256 8a248 248 0 1 0 0 496 248 248 0 1 0 0-496zM371 176.7c-3.7 39.2-19.9 134.4-28.1 178.3-3.5 18.6-10.3 24.8-16.9 25.4-14.4 1.3-25.3-9.5-39.3-18.7-21.8-14.3-34.2-23.2-55.3-37.2-24.5-16.1-8.6-25 5.3-39.5 3.7-3.8 67.1-61.5 68.3-66.7 .2-.7 .3-3.1-1.2-4.4s-3.6-.8-5.1-.5c-2.2 .5-37.1 23.5-104.6 69.1-9.9 6.8-18.9 10.1-26.9 9.9-8.9-.2-25.9-5-38.6-9.1-15.5-5-27.9-7.7-26.8-16.3 .6-4.5 6.7-9 18.4-13.7 72.3-31.5 120.5-52.3 144.6-62.3 68.9-28.6 83.2-33.6 92.5-33.8 2.1 0 6.6 .5 9.6 2.9 2 1.7 3.2 4.1 3.5 6.7 .5 3.2 .6 6.5 .4 9.8z' },
} as const;

const SOCIAL_LINKS = [
  { key: 'vk', href: 'https://vk.com/my_teacher_pro', label: 'MyTeacher.pro во ВКонтакте' },
  { key: 'tg', href: 'https://t.me/MyTeacherPro', label: 'MyTeacher.pro в Telegram' },
] as const;

const SocialLinks = ({ className }: { className: string }) => (
  <div className={className}>
    {SOCIAL_LINKS.map((s) => (
      <a key={s.key} href={s.href} target='_blank' rel='noopener noreferrer' aria-label={s.label} title={s.label}>
        <svg viewBox={SOCIAL_ICONS[s.key].viewBox} width={22} height={22} aria-hidden='true' focusable='false'>
          <path fill='currentColor' d={SOCIAL_ICONS[s.key].d} />
        </svg>
      </a>
    ))}
  </div>
);

/* Колонки как в подвале myteacher: «Математика» разделена на «Математика» и «Экзамены», справка в «О сервисе». */
const COLUMNS: { title: string; links: { href: string; label: string }[] }[] = [
  {
    title: 'Ученикам',
    links: [
      { href: marketingHref('/recommendations'), label: 'Как устроен подбор' },
      { href: marketingHref('/podbor'), label: 'Подобрать репетитора' },
      { href: marketingHref('/podgotovka'), label: 'Собрать план' },
      { href: marketingHref('/zadachi'), label: 'Тест по математике' },
      { href: marketingHref('/guides/zayavka'), label: 'Пример заявки' },
    ],
  },
  {
    title: 'Математика',
    links: [
      { href: marketingHref('/categories/matematika'), label: 'Репетитор по математике' },
      { href: marketingHref('/uslugi'), label: 'Услуги и цены' },
      { href: marketingLinks.probniyUrok, label: 'Пробный урок' },
      { href: marketingHref('/lp/matematika-5-8-klass'), label: '5-8 класс' },
      { href: marketingHref('/lp/uspevaemost-matematika'), label: 'Успеваемость' },
    ],
  },
  {
    title: 'Экзамены',
    links: [
      { href: marketingHref('/lp/oge-matematika'), label: 'ОГЭ' },
      { href: marketingHref('/lp/ege-matematika-baza'), label: 'ЕГЭ база' },
      { href: marketingHref('/lp/ege-matematika-profil'), label: 'ЕГЭ профиль' },
    ],
  },
  {
    title: 'О сервисе',
    links: [
      { href: marketingLinks.about, label: 'О сервисе' },
      { href: marketingLinks.questions, label: 'Вопросы и ответы' },
      { href: marketingLinks.guides, label: 'Как пользоваться' },
    ],
  },
  {
    title: 'Репетиторам',
    links: [
      { href: '/registration?role=tutor', label: 'Регистрация' },
      { href: marketingHref('/guides/registratsiya-repetitora'), label: 'Как начать' },
      { href: marketingLinks.terms, label: 'Условия работы' },
    ],
  },
  {
    title: 'Тех.поддержка',
    links: [
      { href: 'mailto:support@myteacher.pro', label: 'support@myteacher.pro' },
      { href: 'mailto:info@myteacher.pro', label: 'info@myteacher.pro' },
    ],
  },
];

export const Footer = () => (
  <footer className={styles.footer}>
    <div className={styles.top}>
      <div className={styles.brand}>
        <a href={marketingLinks.home} className={styles.logo} aria-label='MyTeacher.pro - на главную'>
          <img src={logo} alt='' width={41} height={41} />
          <span className={styles.word}>MYTEACHER</span>
          <span className={styles.pro}>PRO</span>
        </a>
        <p className={styles.tagline}>
          Математика начинается
          <br />с вашей задачи.
        </p>
        <SocialLinks className={styles.socials} />
      </div>

      <nav className={styles.columns} aria-label='Разделы сайта'>
        {COLUMNS.map((col) => (
          <div key={col.title} className={styles.col}>
            <p className={styles.colTitle}>{col.title}</p>
            {col.links.map((link) => (
              <a key={link.href} href={link.href}>
                {link.label}
              </a>
            ))}
          </div>
        ))}
      </nav>
    </div>

    <div className={styles.rule} />

    <div className={styles.legal}>
      <span>© MYTEACHER.PRO, 2026</span>
      <a href={marketingLinks.terms}>Соглашение</a>
      <a href={marketingLinks.privacy}>Политика конфиденциальности</a>
      <a href={marketingLinks.privacyConsent}>Согласие на обработку данных</a>
      <button type='button' className={styles.cookieSettings} onClick={openCookieSettings}>
        Файлы на устройстве
      </button>
      <span>ИП Веретенников М.В., ИНН 645503402148, ОГРНИП 325645700086537</span>
    </div>
  </footer>
);
